import React, { useMemo, useCallback } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { UserService } from '@duck/api-definitions'
import { Breadcrumbs } from '@duck/ui'
import { UsersTable } from './UsersTable'
import { baseColumns, deriveAccessGroups } from './userAdmin.utils'
import { useTar } from '@/modules'
import { useAdminUsers } from '@/hooks/useAdminUsers'
import { useTableSettings } from '@/hooks/TableSettings/useTableSettings'

const TABLE_ID = 'admin-users'

const defaultVisibleColumns = Object.fromEntries(
  baseColumns.map((col) => [col.key, col.visible])
) as Record<string, boolean>

const FILTER_KEYS = {
  role: 'role',
  isActive: 'isActive',
  isVerified: 'isVerified',
  accessGroups: 'accessGroups',
  search: 'search',
  organizationId: 'organizationId',
} as const

export const UsersManagement: React.FC = () => {
  const { t } = useTar()
  const navigate = useNavigate()

  const {
    sortColumn,
    sortDirection,
    toggleSort,
    isColumnVisible,
    toggleColumnVisibility,
    getColumnFilter,
    setColumnFilter,
    getColumnFilterArray,
    setColumnFilterArray,
    toggleColumnFilterArrayItem,
    clearColumnFilter,
    clearAllSettings,
    hasActiveSettings,
    filters,
    visibleColumns: visibleColumnsRecord,
    filterLabels,
    applyFilterSet,
    setSort,
  } = useTableSettings({
    tableId: TABLE_ID,
    defaultVisibleColumns,
    defaultSort: { columnKey: 'createdAt', direction: 'desc' },
  })

  const sortField = (sortColumn ?? 'createdAt') as keyof UserService.User
  const sortDirectionResolved = sortDirection === 'asc' || sortDirection === 'desc' ? sortDirection : 'desc'

  const visibleColumns = useMemo(
    () => baseColumns.filter((col) => isColumnVisible(col.key)),
    [isColumnVisible]
  )

  const roleFilterArr = getColumnFilterArray(FILTER_KEYS.role)
  const isActiveFilterArr = getColumnFilterArray(FILTER_KEYS.isActive)
  const isVerifiedFilterArr = getColumnFilterArray(FILTER_KEYS.isVerified)
  const accessGroupsFilterArr = getColumnFilterArray(FILTER_KEYS.accessGroups)
  const searchText = getColumnFilter(FILTER_KEYS.search)
  const organizationIdFilterArr = getColumnFilterArray(FILTER_KEYS.organizationId)

  // Build backend query filters from table settings
  const queryFilters = useMemo(() => {
    const qf: {
      role?: UserService.UserRole[]
      isActive?: boolean
      isVerified?: boolean
      accessGroups?: string[]
      search?: string
      organizationId?: string[]
    } = {}

    if (roleFilterArr.length > 0) {
      qf.role = roleFilterArr as UserService.UserRole[]
    }

    // isActive enum filter: single-select boolean. If both selected, don't filter.
    if (isActiveFilterArr.length === 1) {
      qf.isActive = isActiveFilterArr[0] === 'true'
    }

    // isVerified enum filter: single-select boolean. If both selected, don't filter.
    if (isVerifiedFilterArr.length === 1) {
      qf.isVerified = isVerifiedFilterArr[0] === 'true'
    }

    if (accessGroupsFilterArr.length > 0) {
      qf.accessGroups = accessGroupsFilterArr
    }

    if (searchText?.trim()) {
      qf.search = searchText.trim()
    }

    if (organizationIdFilterArr.length > 0) {
      qf.organizationId = organizationIdFilterArr
    }

    return qf
  }, [roleFilterArr, isActiveFilterArr, isVerifiedFilterArr, accessGroupsFilterArr, searchText, organizationIdFilterArr])

  const {
    users,
    hasMore,
    isInitialLoading,
    isFetchingNextPage,
    isError,
    fetchNextPage,
    refetch,
  } = useAdminUsers({
    queryFilters,
    sortField,
    sortDirection: sortDirectionResolved,
  })

  // Derive allAccessGroups from users for the user dialog
  const allAccessGroups = useMemo(() => deriveAccessGroups(users), [users])

  const handleLoadMorePage = useCallback(() => {
    if (!hasMore) return
    void fetchNextPage()
  }, [hasMore, fetchNextPage])

  const handleUserClick = (user: UserService.User) => {
    void navigate({ to: '/admin/users/$id', params: { id: user.id } })
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('challenges.breadcrumbAdmin'), href: '/admin' },
          { label: t('users.userManagement') },
        ]}
        renderLink={(item, children) => <Link to={item.href}>{children}</Link>}
      />
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold leading-7 text-foreground sm:truncate sm:text-3xl sm:tracking-tight">
            {t('users.userManagement')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('users.userManagementDescription')}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { void refetch() }}
            className="px-4 py-2 bg-secondary text-foreground rounded-md text-sm font-medium hover:bg-secondary/80 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {/* Error State */}
      {isError && (
        <div className="rounded-md bg-error/10 border border-error/20 p-4">
          <div className="text-error text-sm">
            {t('common.error')}
          </div>
        </div>
      )}

      {/* Users Table */}
      {!isError && (
        <UsersTable
          users={users}
          totalUsers={users.length}
          visibleColumns={visibleColumns}
          sortField={sortField}
          sortDirection={sortDirectionResolved}
          onSort={toggleSort}
          onUserClick={handleUserClick}
          loadingMode={isInitialLoading}
          hasMore={hasMore}
          onLoadMore={handleLoadMorePage}
          isPageLoading={isFetchingNextPage}
          accessGroupOptions={allAccessGroups}
          filterApi={{
            getColumnFilter,
            setColumnFilter,
            getColumnFilterArray,
            setColumnFilterArray,
            toggleColumnFilterArrayItem,
            clearColumnFilter,
          }}
          hasNoFilterResults={hasActiveSettings() && users.length === 0 && !isInitialLoading}
          onClearFilters={clearAllSettings}
          tableSettings={{
            hasActiveSettings,
            clearAllSettings,
            isColumnVisible,
            toggleColumnVisibility,
            availableColumns: baseColumns.map((col) => ({ key: col.key, label: t(col.label) })),
            tableId: TABLE_ID,
            sortColumn,
            sortDirection,
            filters,
            visibleColumns: visibleColumnsRecord,
            defaultVisibleColumns,
            filterLabels,
            setSort,
            clearColumnFilter,
            applyFilterSet,
          }}
        />
      )}

    </div>
  )
}
